import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { interval } from 'rxjs';
import { AlertController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { filter, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
	providedIn: 'root',
})
export class CheckUpdateService {

	destroyRef = inject(DestroyRef);

	constructor(
		public updates: SwUpdate,
		public alertCtrl: AlertController,
		private router: Router,
	) {
		const timePick = 60 * 1000;

		if (updates.isEnabled) {
			interval(timePick).subscribe(() => updates.checkForUpdate().then(() => console.log('Checking for updates...')));
		}
	}

	public checkForUpdates(): void {
		this.updates.versionUpdates
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap((e) => {
					console.log('Version update available:', e);
					if (e.type === 'VERSION_INSTALLATION_FAILED') {
						console.error('L\'installazione dell\'aggiornamento è fallita:', e.error);
						// Se l'installazione fallisce (spesso per hash mismatch), proviamo a forzare un reload hard
						// per ripulire lo stato e caricare i nuovi asset direttamente dal server
						this.forceHardReload();
					}
				}),
				filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
			)
			.subscribe(() => {
				this.updateApp();
			});
	}

	private forceHardReload() {
		// Cancella le cache e disregistra il Service Worker per risolvere problemi di hash mismatch o file corrotti
		caches.keys().then((keyList) => {
			return Promise.all(keyList.map((key) => caches.delete(key)));
		}).then(() => {
			return navigator.serviceWorker.getRegistrations();
		}).then((registrations) => {
			return Promise.all(registrations.map((registration) => registration.unregister()));
		}).then(() => {
			console.log('Reloading application due to update failure...');
			window.location.reload();
		}).catch(() => {
			window.location.reload();
		});
	}

	updateApp() {
		this.alertCtrl
			.create({
				header: 'Agenda è stata aggiornata!',
				backdropDismiss: false,
				message: `Premere OK per confermare.`,
				buttons: [
					{
						text: 'Ok',
						handler: () => {
							console.log('Updating to new version...');

							// Cancella tutte le cache manualmente prima di aggiornare
							caches.keys().then((keyList) => {
								return Promise.all(keyList.map((key) => caches.delete(key)));
							}).then(() => {
								console.log('Cache cancellata, disregistrazione Service Worker...');

								// Disregistra tutti i Service Worker
								return navigator.serviceWorker.getRegistrations();
							}).then((registrations) => {
								return Promise.all(registrations.map((registration) => registration.unregister()));
							}).then(() => {
								console.log('Service Worker disregistrato, attivazione aggiornamento...');
								return this.updates.activateUpdate();
							}).then(() => {
								console.log('Ricaricamento hard della pagina (come Ctrl+F5)...');
								// Hard reload che bypassa completamente la cache (come Ctrl+F5)
								window.location.href = window.location.href;
								window.location.reload();
							}).catch((error) => {
								console.error('Errore durante l\'aggiornamento:', error);
								// In caso di errore, forza comunque il reload hard
								window.location.href = window.location.href;
								window.location.reload();
							});
						},
					},
				],
			})
			.then((ElRef) => ElRef.present());
	}
}
